import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Home, Target, Briefcase, Image, FileText, User, LayoutGrid } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import MobileAIAgentButton from './MobileAIAgentButton';
import NavItem from '@/components/layout/NavItem';
import { usePinnedAppsWithDetails } from '@/hooks/app-library/use-user-pinned-apps';
import { getIconByName } from '@/hooks/app-library/utils';
import { getActualRoute } from '@/utils/routeMapper';

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getPinnedAppDetails, isLoading } = usePinnedAppsWithDetails();
  const { isModalOpen, closeModal } = useLocalStorageState();

  // Custom breakpoint detection for 1024px (lg breakpoint)
  const [isBelow1024px, setIsBelow1024px] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024;
  });

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setIsBelow1024px(e.matches);
    };

    mql.addEventListener('change', handleChange);
    setIsBelow1024px(mql.matches);

    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  // Handler to close mobile chat when any nav item is clicked
  const handleNavItemClick = () => {
    if (isModalOpen) {
      closeModal();
    }
  };

  // Get pinned apps details (now 4 apps)
  const pinnedApp1 = getPinnedAppDetails(1);
  const pinnedApp2 = getPinnedAppDetails(2);
  const pinnedApp3 = getPinnedAppDetails(3);
  const pinnedApp4 = getPinnedAppDetails(4);

  // Fallback nav items if pinned apps are not loaded (now using consistent naming)
  const fallbackNavItems = [
    {
      name: 'Documents',
      to: '/private-text-generator',
      icon: React.createElement(getIconByName('FileText'), { size: 20 })
    },
    {
      name: 'Research',
      to: '/private-research-generator',
      icon: React.createElement(getIconByName('Microscope'), { size: 20 })
    },
    {
      name: 'Images',
      to: '/private-image-generator',
      icon: React.createElement(getIconByName('Images'), { size: 20 })
    },
    // {
    //   name: 'Videos',
    //   to: '/video-generator',
    //   icon: React.createElement(getIconByName('Clapperboard'), { size: 20 })
    // }
  ];

  // Create nav items from pinned apps (now 4 apps)
  const createNavItems = () => {
    if (isLoading) {
      return fallbackNavItems;
    }

    const navItems = [];
    
    // Add all 4 pinned apps
    [pinnedApp1, pinnedApp2, pinnedApp3, pinnedApp4].forEach(app => {
      if (app) {
        // Use display_name for consistent naming across all screen sizes
        const displayName = app.display_name || app.name;

        navItems.push({
          name: displayName,
          to: getActualRoute(app.route),
          icon: React.createElement(getIconByName(app.icon_name), { size: 20 })
        });
      }
    });

    return navItems;
  };

  const navItems = createNavItems();

  return (
    <nav className="w-full">
      <ul className="flex justify-around items-center w-full">
        {/* First two navigation items */}
        {navItems.slice(0, 2).map((item) => (
          <li key={item.name}>
            <NavItem
              to={item.to}
              label={item.name}
              icon={item.icon}
              isActive={location.pathname.startsWith(item.to)}
              onClick={handleNavItemClick}
            />
          </li>
        ))}
        
        {/* AI Agent button in the middle */}
        <li className="flex justify-center">
          <MobileAIAgentButton />
        </li>
        
        {/* Last two navigation items (3rd and 4th apps) */}
        {navItems.slice(2, 4).map((item) => (
          <li key={item.name}>
            <NavItem
              to={item.to}
              label={item.name}
              icon={item.icon}
              isActive={location.pathname.startsWith(item.to)}
              onClick={handleNavItemClick}
            />
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default MobileBottomNav;
